import { Box, Grid, Typography, lighten, useMediaQuery } from '@mui/material';
import React, { useMemo } from 'react';
import moment from 'moment';
import { theme } from '../../../../shared/theme';
import { CustomChip } from '../../../../layout/styledcomponents/CustomChip';

type ReservationSummarySectionProps = {
    getReservationDetails: any;
    getIndividualAsset: any;
    loggedInUser: any;
    startDate: string;
    endDate: string;
};

const formatTimeWithAmPm = (t: any) => {
    const raw = String(t || '').trim();
    if (!raw || raw === '-') return '-';
    const m = moment(
        raw,
        ['H:mm', 'HH:mm', 'h:mm A', 'h:mma', 'hh:mm A'],
        true,
    );
    if (m.isValid()) return m.format('h:mm A');
    return raw;
};

export default function ReservationSummaryBlock({
    getReservationDetails,
    getIndividualAsset,
}: ReservationSummarySectionProps) {
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isNarrowSidebar = useMediaQuery('(max-width: 1200px)');

    const status = getReservationDetails?.status ?? '';
    const formattedStatus = useMemo(() => {
        const s = String(getReservationDetails?.status ?? '');
        if (!s) return '';
        return s.charAt(0).toUpperCase() + s.slice(1);
    }, [getReservationDetails?.status]);

    const showCheckTimes = !(
        getReservationDetails?.bookerType === 'owner' &&
        getReservationDetails?.onBehalf === false
    );

    const checkinTime = showCheckTimes
        ? getReservationDetails?.bookingInfo?.checkinTime
        : '-';

    const checkoutTime = showCheckTimes
        ? getReservationDetails?.bookingInfo?.checkoutTime
        : '-';

    const isCommercial = getIndividualAsset?.type === 'commercial';
    const startDateRaw = getReservationDetails?.startDate;
    const endDateRaw = getReservationDetails?.endDate;

    const dateRange = useMemo(() => {
        if (!startDateRaw || !endDateRaw) return '-';
        const s = moment(startDateRaw);
        const e = moment(endDateRaw);
        if (!s.isValid() || !e.isValid()) return '-';
        if (s.isSame(e, 'day')) return s.format('ddd D MMM YYYY');
        if (s.isSame(e, 'month'))
            return `${s.format('ddd D')} – ${e.format('ddd D MMM YYYY')}`;
        return `${s.format('ddd D MMM')} – ${e.format('ddd D MMM YYYY')}`;
    }, [startDateRaw, endDateRaw]);

    const timeRange = useMemo(() => {
        if (!showCheckTimes) return '-';
        const s = formatTimeWithAmPm(checkinTime);
        const e = formatTimeWithAmPm(checkoutTime);
        if (s === '-' && e === '-') return '-';
        if (s !== '-' && e !== '-') return `${s} → ${e}`;
        return s !== '-' ? s : e;
    }, [checkinTime, checkoutTime, showCheckTimes]);

    const getStatusSx = (s: string) => {
        switch (s) {
            case 'approved':
                return {
                    bgcolor: lighten(theme.palette.yellow.main, 0.78),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.yellow.main, 0.35),
                };
            case 'pending':
                return {
                    bgcolor: lighten(theme.palette.primary.main, 0.76),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.primary.main, 0.32),
                };
            case 'cancelled':
            case 'declined':
                return {
                    bgcolor: lighten(theme.palette.error.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.error.main, 0.3),
                };
            case 'completed':
                return {
                    bgcolor: lighten(theme.palette.info.main, 0.82),
                    color: theme.palette.dark.main,
                    borderColor: lighten(theme.palette.info.main, 0.3),
                };
            default:
                return {
                    bgcolor: theme.palette.custom.cardActionBackground,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.custom.lightBorderColor,
                };
        }
    };

    const columns = isMobile ? 2 : isNarrowSidebar ? 1 : 2;

    const infoCardSx = {
        width: '100%',
        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
        backgroundColor: theme.palette.neutral.main,
        borderRadius: 2,
        p: 2,
    };

    const openRecurringReservation = () => {
        const recurringId = getReservationDetails?.recurringId;
        if (!recurringId) return;

        const url = `/reservations/ai-assistant/${recurringId}`;

        const width = 1200;
        const height = 800;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        window.open(
            url,
            'recurringReservation',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
        );
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    mb: 1.25,
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        component='h2'
                        variant='body1'
                        sx={{
                            fontWeight: 900,
                            letterSpacing: 0.2,
                            fontSize: 15,
                            lineHeight: 1.15,
                        }}
                    >
                        Reservation #{getReservationDetails?.id}
                    </Typography>
                </Box>
                <Box sx={{ flexShrink: 0, mt: '-2px' }}>
                    <CustomChip
                        label={
                            <Typography
                                variant='body3'
                                sx={{ fontSize: 10.5, fontWeight: 900 }}
                            >
                                {formattedStatus}
                            </Typography>
                        }
                        variant='outlined'
                        sx={{
                            ...getStatusSx(status),
                            borderWidth: 1,
                            height: 18,
                            px: 0.6,
                            minWidth: 72,
                            borderRadius: 999,
                            '& .MuiChip-label': { px: 0.6 },
                        }}
                    />
                </Box>
            </Box>
            {getReservationDetails?.recurringId ? (
                <Box
                    role='button'
                    tabIndex={0}
                    onClick={openRecurringReservation}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                            openRecurringReservation();
                    }}
                    sx={{
                        mb: 1.25,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                        backgroundColor: theme.palette.neutral.main,
                        px: 1.5,
                        py: 1.1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        cursor: 'pointer',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: 4,
                            backgroundColor: theme.palette.primary.main,
                        },
                        '&:hover': {
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                        },
                    }}
                >
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 12,
                                fontWeight: 900,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                color: theme.palette.text.primary,
                            }}
                        >
                            Recurring reservation · #
                            {getReservationDetails?.recurringId}
                        </Typography>

                        <Typography
                            sx={{
                                mt: 0.2,
                                fontSize: 12,
                                fontWeight: 650,
                                color: theme.palette.text.secondary,
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            Created via 7DAY AI Assistant
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            flex: '0 0 auto',
                            borderRadius: 999,
                            border: `1px solid ${theme.palette.custom.lightBorderColor}`,
                            backgroundColor:
                                theme.palette.custom.cardActionBackground,
                            px: 1,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <Typography sx={{ fontSize: 11, fontWeight: 900 }}>
                            View
                        </Typography>
                    </Box>
                </Box>
            ) : null}
            <Grid container spacing={1.25} columns={columns}>
                <Grid item xs={columns}>
                    <Box sx={infoCardSx}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                fontWeight: 800,
                                letterSpacing: 0.35,
                                color: theme.palette.text.secondary,
                                lineHeight: 1.2,
                            }}
                        >
                            {isCommercial
                                ? 'Access window'
                                : 'Check-in → Check-out'}
                        </Typography>

                        <Typography
                            variant='body3'
                            sx={{
                                fontSize: 14,
                                fontWeight: 900,
                                color: theme.palette.text.primary,
                                mt: 0.75,
                                lineHeight: 1.2,
                            }}
                        >
                            {dateRange}
                        </Typography>

                        {timeRange !== '-' ? (
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: theme.palette.text.secondary,
                                    mt: 0.4,
                                    lineHeight: 1.2,
                                }}
                            >
                                {timeRange}
                            </Typography>
                        ) : null}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
}
